import type { HsDecodedMesh, HsOriginalMeshSource, HsVertexColorGroupView } from '@sibe/hyperscene';

export class HsMeshMaterialValidationError extends Error {}

export function hsRestoreCanonicalMesh(mesh: HsDecodedMesh): HsDecodedMesh {
  if (mesh.canonicalVertexOrder === true) {
    return mesh;
  }
  const order: ArrayLike<number> | undefined = mesh.canonicalIndices;
  if (order === undefined || order.length !== mesh.indices.length) {
    throw new Error(`Mesh ${mesh.geometryId} requires canonical vertex order metadata.`);
  }
  // Typed arrays: these copies are retained per composite mesh and uploaded as Float32 anyway.
  const sourceUvs: ArrayLike<number> = mesh.uvs ?? [];
  const hasUvs: boolean = sourceUvs.length > 0;
  const vertices: Float32Array = new Float32Array(order.length * 3);
  const normals: Float32Array = new Float32Array(order.length * 3);
  const uvs: Float32Array = new Float32Array(hasUvs ? order.length * 2 : 0);
  const indices: Uint32Array = new Uint32Array(order.length);
  for (let target: number = 0; target < order.length; target++) {
    const index: number = order[target];
    if (!Number.isInteger(index) || index < 0 || index * 3 + 2 >= mesh.vertices.length) {
      throw new Error(`Invalid canonical vertex index in mesh ${mesh.geometryId}.`);
    }
    for (let axis: number = 0; axis < 3; axis++) {
      vertices[target * 3 + axis] = mesh.vertices[index * 3 + axis];
      normals[target * 3 + axis] = mesh.normals[index * 3 + axis] ?? 0;
    }
    if (hasUvs) {
      uvs[target * 2] = sourceUvs[index * 2];
      uvs[target * 2 + 1] = sourceUvs[index * 2 + 1];
    }
    indices[target] = target;
  }
  return {
    geometryId: mesh.geometryId,
    canonicalVertexOrder: true,
    vertices,
    normals,
    uvs,
    indices,
  };
}

export function hsCorrectOriginalMesh(mesh: HsDecodedMesh, source: HsOriginalMeshSource): HsDecodedMesh {
  if (mesh.geometryId !== source.originalGeometryId) {
    throw new Error(`Original mesh ${source.originalGeometryId} is missing.`);
  }
  mesh = hsRestoreCanonicalMesh(mesh);
  const matrix: number[] = source.correctionMatrix;
  const normalMatrix: number[] = source.normalCorrectionMatrix;
  if (
    [matrix, normalMatrix].some(
      (value: number[]) => value.length !== 16 || value.some((element: number) => !Number.isFinite(element))
    )
  ) {
    throw new Error(`Invalid original mesh transform for node ${source.nodeId}.`);
  }
  const vertices: Float32Array = new Float32Array(mesh.vertices.length);
  const normals: Float32Array = new Float32Array(mesh.normals.length);
  for (let offset: number = 0; offset < vertices.length; offset += 3) {
    const x: number = mesh.vertices[offset];
    const y: number = mesh.vertices[offset + 1];
    const z: number = mesh.vertices[offset + 2];
    for (let axis: number = 0; axis < 3; axis++) {
      vertices[offset + axis] = matrix[axis] * x + matrix[axis + 4] * y + matrix[axis + 8] * z + matrix[axis + 12];
    }
  }
  for (let offset: number = 0; offset < normals.length; offset += 3) {
    const x: number = mesh.normals[offset];
    const y: number = mesh.normals[offset + 1];
    const z: number = mesh.normals[offset + 2];
    const nx: number = normalMatrix[0] * x + normalMatrix[4] * y + normalMatrix[8] * z;
    const ny: number = normalMatrix[1] * x + normalMatrix[5] * y + normalMatrix[9] * z;
    const nz: number = normalMatrix[2] * x + normalMatrix[6] * y + normalMatrix[10] * z;
    const length: number = Math.hypot(nx, ny, nz);
    normals[offset] = length === 0 ? 0 : nx / length;
    normals[offset + 1] = length === 0 ? 0 : ny / length;
    normals[offset + 2] = length === 0 ? 0 : nz / length;
  }
  return { ...mesh, vertices, normals };
}

export interface HsMeshMaterialPart {
  mesh: HsDecodedMesh;
  rgba: number[];
}

export interface HsMeshMaterialPartInfo {
  parentRenderKey: string;
  rgba: number[];
}

export interface HsCachedMeshMaterialParts {
  vertices: HsDecodedMesh['vertices'];
  normals: HsDecodedMesh['normals'];
  indices: HsDecodedMesh['indices'];
  groups: HsVertexColorGroupView[];
  rgba: number[];
  parts: HsMeshMaterialPart[];
  /** Keeps the shared split alive while this render key uses it; see HsSharedMeshMaterialSplits. */
  shared?: object;
  errorMessage?: string;
}

interface MaterialPartBuffers {
  vertices: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
}

function validateMeshUvs(mesh: HsDecodedMesh, vertexCount: number): void {
  if (mesh.uvs !== undefined && mesh.uvs.length > 0 && mesh.uvs.length !== vertexCount * 2) {
    throw new HsMeshMaterialValidationError(`Invalid UV coordinates in composite mesh ${mesh.geometryId}.`);
  }
}

interface VertexMaterials {
  palette: number[][];
  /** Palette index per vertex, or -1 for vertices that keep the base color (palette index 0). */
  vertexMaterials: Int32Array;
}

function assignVertexMaterials(
  mesh: HsDecodedMesh,
  vertexCount: number,
  rgba: number[],
  groups: HsVertexColorGroupView[]
): VertexMaterials {
  const palette: number[][] = [rgba];
  const paletteByColor: Map<string, number> = new Map([[rgba.join(','), 0]]);
  const vertexMaterials: Int32Array = new Int32Array(vertexCount).fill(-1);
  for (const group of groups) {
    const { startVertexIndex: start, endVertexIndex: end, color } = group;
    if (
      start === undefined ||
      end === undefined ||
      !Number.isInteger(start) ||
      !Number.isInteger(end) ||
      start < 0 ||
      end < start ||
      end >= vertexCount ||
      color === undefined ||
      color.length !== 4 ||
      color.some((value: number) => !Number.isFinite(value))
    ) {
      throw new HsMeshMaterialValidationError(
        `Invalid vertex material range in mesh ${mesh.geometryId}: ${start}-${end}, vertices=${vertexCount}.`
      );
    }
    const colorKey: string = color.join(',');
    let materialIndex: number | undefined = paletteByColor.get(colorKey);
    if (materialIndex === undefined) {
      materialIndex = palette.length;
      paletteByColor.set(colorKey, materialIndex);
      palette.push(color);
    }
    for (let vertex: number = start; vertex <= end; vertex++) {
      if (vertexMaterials[vertex] !== -1 && vertexMaterials[vertex] !== materialIndex) {
        throw new HsMeshMaterialValidationError(`Overlapping vertex material ranges in mesh ${mesh.geometryId}.`);
      }
      vertexMaterials[vertex] = materialIndex;
    }
  }
  return { palette, vertexMaterials };
}

export function hsSplitMeshByMaterial(
  mesh: HsDecodedMesh,
  rgba: number[],
  groups: HsVertexColorGroupView[]
): HsMeshMaterialPart[] {
  mesh = hsRestoreCanonicalMesh(mesh);
  const vertexCount: number = mesh.vertices.length / 3;
  if (!Number.isInteger(vertexCount) || mesh.indices.length % 3 !== 0) {
    throw new HsMeshMaterialValidationError(`Invalid composite mesh ${mesh.geometryId}.`);
  }
  validateMeshUvs(mesh, vertexCount);
  const { palette, vertexMaterials }: VertexMaterials = assignVertexMaterials(mesh, vertexCount, rgba, groups);
  // Two passes over typed arrays: composite meshes are split for every visible render key, and growing
  // plain arrays here was the largest source of garbage while loading the large demo scene.
  // Every vertex belongs to exactly one material, so one remap array serves all parts.
  const triangleCount: number = mesh.indices.length / 3;
  const partByMaterial: Int32Array = new Int32Array(palette.length).fill(-1);
  const partMaterials: number[] = [];
  const partVertexCounts: number[] = [];
  const partIndexCounts: number[] = [];
  const remap: Int32Array = new Int32Array(vertexCount).fill(-1);
  for (let triangle: number = 0; triangle < triangleCount; triangle++) {
    const offset: number = triangle * 3;
    for (let corner: number = 0; corner < 3; corner++) {
      const index: number = mesh.indices[offset + corner];
      if (!Number.isInteger(index) || index < 0 || index >= vertexCount) {
        throw new HsMeshMaterialValidationError(`Invalid triangle in composite mesh ${mesh.geometryId}.`);
      }
    }
    const materialIndex: number = Math.max(0, vertexMaterials[mesh.indices[offset]]);
    if (
      Math.max(0, vertexMaterials[mesh.indices[offset + 1]]) !== materialIndex ||
      Math.max(0, vertexMaterials[mesh.indices[offset + 2]]) !== materialIndex
    ) {
      throw new HsMeshMaterialValidationError(`Triangle spans different vertex materials in mesh ${mesh.geometryId}.`);
    }
    let part: number = partByMaterial[materialIndex];
    if (part === -1) {
      part = partMaterials.length;
      partByMaterial[materialIndex] = part;
      partMaterials.push(materialIndex);
      partVertexCounts.push(0);
      partIndexCounts.push(0);
    }
    partIndexCounts[part] += 3;
    for (let corner: number = 0; corner < 3; corner++) {
      const index: number = mesh.indices[offset + corner];
      if (remap[index] === -1) {
        remap[index] = partVertexCounts[part]++;
      }
    }
  }
  const hasUvs: boolean = mesh.uvs !== undefined && mesh.uvs.length > 0;
  const sourceUvs: ArrayLike<number> = mesh.uvs ?? [];
  const parts: MaterialPartBuffers[] = partMaterials.map((_: number, part: number) => ({
    vertices: new Float32Array(partVertexCounts[part] * 3),
    normals: new Float32Array(partVertexCounts[part] * 3),
    uvs: new Float32Array(hasUvs ? partVertexCounts[part] * 2 : 0),
    indices: new Uint32Array(partIndexCounts[part]),
  }));
  for (let vertex: number = 0; vertex < vertexCount; vertex++) {
    const target: number = remap[vertex];
    if (target === -1) {
      continue;
    }
    const part: MaterialPartBuffers = parts[partByMaterial[Math.max(0, vertexMaterials[vertex])]];
    for (let axis: number = 0; axis < 3; axis++) {
      part.vertices[target * 3 + axis] = mesh.vertices[vertex * 3 + axis];
      part.normals[target * 3 + axis] = mesh.normals[vertex * 3 + axis] ?? 0;
    }
    if (hasUvs) {
      part.uvs[target * 2] = sourceUvs[vertex * 2];
      part.uvs[target * 2 + 1] = sourceUvs[vertex * 2 + 1];
    }
  }
  const written: Int32Array = new Int32Array(parts.length);
  for (let offset: number = 0; offset < mesh.indices.length; offset++) {
    const index: number = mesh.indices[offset];
    const part: number = partByMaterial[Math.max(0, vertexMaterials[index])];
    parts[part].indices[written[part]++] = remap[index];
  }
  return parts.map((part: MaterialPartBuffers, index: number) => ({
    mesh: {
      geometryId: `${mesh.geometryId}|material-part:${partMaterials[index]}`,
      ...part,
      canonicalVertexOrder: true,
    },
    rgba: palette[partMaterials[index]],
  }));
}

interface SharedMeshMaterialSplit {
  sourceGeometryId: string;
  normals: HsDecodedMesh['normals'];
  indices: HsDecodedMesh['indices'];
  uvs: HsDecodedMesh['uvs'];
  canonicalIndices: HsDecodedMesh['canonicalIndices'];
  canonicalVertexOrder: HsDecodedMesh['canonicalVertexOrder'];
  groups: HsVertexColorGroupView[];
  rgba: number[];
  parts: HsMeshMaterialPart[];
}

export interface HsSharedMeshMaterialSplitResult {
  parts: HsMeshMaterialPart[];
  /** Hold this for as long as `parts` is used, or the next request for the same mesh splits it again. */
  shared: object;
}

/**
 * Shares split results between render keys that reuse one decoded mesh with the same material, so
 * instanced composite meshes are split and retained once instead of once per render key.
 * Results are held weakly: each lives only while some caller still holds its `shared` handle.
 */
export class HsSharedMeshMaterialSplits {
  private readonly splitsByVertices: WeakMap<object, WeakRef<SharedMeshMaterialSplit>[]> = new WeakMap();

  public split(mesh: HsDecodedMesh, rgba: number[], groups: HsVertexColorGroupView[]): HsSharedMeshMaterialSplitResult {
    const refs: WeakRef<SharedMeshMaterialSplit>[] = this.splitsByVertices.get(mesh.vertices) ?? [];
    let shared: SharedMeshMaterialSplit | undefined;
    for (let index: number = refs.length - 1; index >= 0; index--) {
      const candidate: SharedMeshMaterialSplit | undefined = refs[index].deref();
      if (candidate === undefined) {
        refs.splice(index, 1);
      } else if (
        candidate.normals === mesh.normals &&
        candidate.indices === mesh.indices &&
        candidate.uvs === mesh.uvs &&
        candidate.canonicalIndices === mesh.canonicalIndices &&
        candidate.canonicalVertexOrder === mesh.canonicalVertexOrder &&
        candidate.groups === groups &&
        candidate.rgba === rgba
      ) {
        shared = candidate;
      }
    }
    if (shared === undefined) {
      shared = {
        sourceGeometryId: mesh.geometryId,
        normals: mesh.normals,
        indices: mesh.indices,
        uvs: mesh.uvs,
        canonicalIndices: mesh.canonicalIndices,
        canonicalVertexOrder: mesh.canonicalVertexOrder,
        groups,
        rgba,
        parts: hsSplitMeshByMaterial(mesh, rgba, groups),
      };
      refs.push(new WeakRef(shared));
      this.splitsByVertices.set(mesh.vertices, refs);
    }
    if (shared.sourceGeometryId === mesh.geometryId) {
      return { parts: shared.parts, shared };
    }
    const sourcePrefixLength: number = shared.sourceGeometryId.length;
    return {
      parts: shared.parts.map((part: HsMeshMaterialPart) => ({
        mesh: { ...part.mesh, geometryId: mesh.geometryId + part.mesh.geometryId.slice(sourcePrefixLength) },
        rgba: part.rgba,
      })),
      shared,
    };
  }
}
